import Uppy, { UploadResult, UploadedUppyFile } from '@uppy/core';
import Russian from '@uppy/locales/lib/ru_RU';
import { DashboardModal } from '@uppy/react';
import Webcam from '@uppy/webcam';
import { BrowserMultiFormatReader } from '@zxing/library';
import { forEach } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Don't forget the CSS: core and the UI components + plugins you are using.
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import '@uppy/webcam/dist/style.min.css';

import { usePrevious } from 'hooks';
import { qrUtils } from 'utils';

const uppy = new Uppy({
  autoProceed: false,
  allowMultipleUploads: false,
  restrictions: {
    maxNumberOfFiles: 1,
    allowedFileTypes: ['image/*', 'application/pdf'], // Only allow image files and pdf
    maxFileSize: 1024 * 1024 * 3, // 3MB max file size
  },
}).use(Webcam);

type Props = {
  open: boolean;
  onClose: (value?: string) => void;
};

export const UploadQRDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const prevOpen = usePrevious(open);

  const removeFiles = useCallback(() => {
    const files = uppy.getFiles();
    forEach(files, (file) => {
      uppy.removeFile(file.id);
    });
  }, []);

  const handleUpload = useCallback(
    (file: UploadedUppyFile<any, any>) => {
      const codeReader = new BrowserMultiFormatReader();
      const originalImage = new Image();
      originalImage.src = URL.createObjectURL(file.data);

      originalImage.onload = async () => {
        let decodedText = await qrUtils.decodeFromImage(
          codeReader,
          originalImage,
        );

        if (!decodedText) {
          const preprocessedImage = await qrUtils.preprocessImage(
            originalImage,
          );
          decodedText = await qrUtils.decodeFromImage(
            codeReader,
            preprocessedImage,
          );
        }

        if (decodedText) {
          onClose(decodedText);
        } else {
          uppy.info(
            t('components.upload_qr_dialog.parse_error'),
            'error',
            3000,
          );
        }
      };
    },
    [t, onClose],
  );

  const handleFileComplete = useCallback(
    (result: UploadResult<Record<string, unknown>>) => {
      const file = result.successful[0];
      handleUpload(file);
    },
    [handleUpload],
  );

  useEffect(() => {
    if (!open && prevOpen) {
      removeFiles();
    }
  }, [open, prevOpen, removeFiles]);

  useEffect(() => {
    uppy.on('complete', handleFileComplete);
    return () => {
      uppy.off('complete', handleFileComplete);
    };
  }, [handleFileComplete]);

  return (
    <DashboardModal
      open={open}
      uppy={uppy}
      plugins={['Webcam']}
      locale={Russian}
      proudlyDisplayPoweredByUppy={false}
      onRequestClose={onClose}
    />
  );
};
